import { useEffect, useRef, useState, useMemo } from 'react';
import { FlightManifestService } from '@/services/FlightManifestService';

/**
 * Hook that resolves IATA codes to airport names via bulk lookup.
 * Returns a map of IATA -> airport name, auto-fetching any new codes.
 */
export function useAirportNames(iataCodes: string[], service: FlightManifestService) {
	const [nameMap, setNameMap] = useState<Record<string, string>>({});
	const fetchedRef = useRef<Set<string>>(new Set());

	// Deduplicate and find codes we haven't fetched yet
	const newCodes = useMemo(() => {
		const unique = [...new Set(iataCodes.filter(c => c && c.length >= 2 && c.length <= 4))];
		return unique.filter(c => !fetchedRef.current.has(c));
	}, [iataCodes]);

	useEffect(() => {
		if (newCodes.length === 0) return;

		// Mark as fetched immediately to avoid duplicate requests
		for (const c of newCodes) fetchedRef.current.add(c);

		service.lookupAirports(newCodes).then(map => {
			if (Object.keys(map).length > 0) {
				setNameMap(prev => ({ ...prev, ...map }));
			}
		}).catch(() => { /* silently fail - tooltips just won't show */ });
	}, [newCodes, service]);

	return nameMap;
}
